import {DOCUMENT_STATE, DOSSIER_TYPE} from 'front-onceforall-core/dist/utils/documents';
import {aDocument, aDocumentType, aDossier} from 'front-onceforall-core/dist/testUtils/testDocumentUtils';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';

export const keyStorageDossierCountry = 'dossier-country';

export function setFirstDossierCountry(dossierCountry) {
  localStorage.setItem(keyStorageDossierCountry, dossierCountry);
}

export function getFirstDossierCountry(countries, clientCountry, subcontractorCountry) {
  if (countries.length === 0) {
    return null;
  }

  const storageCountry = localStorage.getItem(keyStorageDossierCountry);

  if (countries.includes(storageCountry)) {
    return storageCountry;
  }

  if (countries.includes(clientCountry)) {
    return clientCountry;
  }

  if (countries.includes(subcontractorCountry)) {
    return subcontractorCountry;
  }

  return countries[0];
}

export function hasStandardFiles(documents) {
  return documents.filter(document => [
    DOCUMENT_STATE.VALID,
    DOCUMENT_STATE.SIGNED,
    DOCUMENT_STATE.ALMOST_EXPIRED,
    DOCUMENT_STATE.REJECTED,
    DOCUMENT_STATE.EXPIRED,
    DOCUMENT_STATE.AWAITING_VALIDATION,
    DOCUMENT_STATE.NOT_APPLICABLE
  ].some(documentState => documentState === document.state))
    .filter(document => document.dossiers.filter(
      dossier => dossier.type === DOSSIER_TYPE.LEGAL || dossier.type === DOSSIER_TYPE.BUSINESS
    ).length > 0)
    .length > 0;
}

export function hasFiles(documents) {
  return documents.some(doc => doc.hasFile);
}

export function hasValidOrNotApplicableFiles(documents) {
  return documents.filter(doc => doc.hasFile)
    .some(
      document => document.state === DOCUMENT_STATE.VALID || document.state === DOCUMENT_STATE.ALMOST_EXPIRED || document.state === DOCUMENT_STATE.SIGNED || document.state === DOCUMENT_STATE.NOT_APPLICABLE
    );
}

export function isRejected(document) {
  return Boolean(document.state === DOCUMENT_STATE.REJECTED);
}

export function getDocumentName(documentTypeName) {
  // Normalize the string to NFD form and remove diacritical marks
  let pdfName = documentTypeName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Replace § character
  pdfName = pdfName.replace(/§/g, '$');
  // Replace specific characters like space,/,(,),.
  pdfName = pdfName.replace(' / ', '--');
  pdfName = pdfName.replace('/', '-');
  pdfName = pdfName.replace(/\s/g, '_');
  return pdfName.replace(/[()'.]/g, '');
}

export function filterDocumentsByType(documents, dossier = '') {
  return documents.filter(document => document.type.dossier === dossier);
}

export function getLegalDocuments(documents) {
  return filterDocumentsByType(documents, DOSSIER_TYPE.LEGAL);
}

export function getBusinessDocuments(documents) {
  return filterDocumentsByType(documents, DOSSIER_TYPE.BUSINESS);
}

export function getAdditionalDocuments(documents) {
  return filterDocumentsByType(documents, DOSSIER_TYPE.ADDITIONAL);
}

export function getProjectDocuments(documents) {
  return filterDocumentsByType(documents, DOSSIER_TYPE.PROJECT);
}

export function getDistinctCountries(documents) {
  return [...new Set(documents.map(document => document.type.country))];
}

export function getDocumentsByCountry(documents, country) {
  return documents.filter(document => document.type.country === country);
}

export function getFakeDocumentsByCountry(country) {
  function buildFakeDocument(documentName, documentCode) {
    return aDocument({
      state: 'MISSING',
      certifyingThirdParty: null,
      issuingThirdParty: null,
      dossiers: [aDossier({type: 'LEGAL'})],
      type: aDocumentType({
        dossier: 'LEGAL',
        name: documentName,
        code: documentCode,
        country
      }),
      overallStatusDocument: true
    });
  }

  const frenchFakeDocuments = [
    buildFakeDocument('Justificatif d\'immatriculation', 'FR_REG'),
    buildFakeDocument('Attestation Sociale de vigilance Etranger', 'COV_VIGI_ETRANGER'),
    buildFakeDocument('Liste des salariés étrangers soumis à autorisation de travail', 'FOREIGN_EMPLOYEES')
  ];

  const germanFakeDocuments = [
    buildFakeDocument('Handelsregisterauszug', 'REG_TRADE'),
    buildFakeDocument('Gewerbeanmeldung', 'REG_BUSINESS'),
    buildFakeDocument('Eintragung IHK', 'REG_COMPANY'),
    buildFakeDocument('Krankenkassen (Unbedenklichkeitsbescheinigung)', 'PE_HEALTH'),
    buildFakeDocument('Berufsgenossenschaft (Qualifizierte Unbedenklichkeitsbescheinigung)', 'INS_ASSOCIATION'),
    buildFakeDocument('Qualifizierter Haftpflichtversicherungsnachweis', 'INS_LIABILITY'),
    buildFakeDocument('Bescheinigung in Steuersachen', 'TAX_OFFICE')
  ];

  switch (country) {
    case COUNTRY.FRANCE:
      return frenchFakeDocuments;
    case COUNTRY.GERMANY:
      return germanFakeDocuments;
    default:
      return [];
  }
}
